import React, { useEffect, useState } from 'react';
import axios from 'axios';
import '../../styles/ui.css';

export default function PendingFinalApproval() {
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [details, setDetails] = useState({});
  const [notes, setNotes] = useState({});
  const [saving, setSaving] = useState({});

  const token = localStorage.getItem('token');
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api';
  const API_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, '');
  const withToken = (url) => {
    const token = localStorage.getItem('token');
    if (!token || !url.includes('/api/files/')) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}token=${encodeURIComponent(token)}`;
  };

  const normalizeDocuments = (docsRaw) => {
    if (!Array.isArray(docsRaw)) return [];
    return docsRaw.map((d, idx) => {
      const document_id = d.document_id || d.id || d.doc_id || d.file_id || `${idx}`;
      const document_name = d.document_name || d.name || d.file_name || d.filename || d.title || `Document ${idx + 1}`;
      let path = d.path || d.url || d.file_path || d.fileurl || d.location || '';
      if (path && !/^https?:\/\//.test(path)) {
        path = `${API_ORIGIN}${path}`;
      }
      return { document_id, document_name, path };
    });
  };

  const fetchList = async () => {
    const { data } = await axios.get(
      `${API_BASE_URL}/proposals/list?status=${encodeURIComponent('Ready for Final Approval')}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setItems(data || []);
  };

  useEffect(() => { fetchList(); }, []);

  const toggleDetails = async (p) => {
    const id = p.proposal_id;
    const isOpen = !!expanded[id];
    setExpanded((prev) => ({ ...prev, [id]: !isOpen }));

    if (!isOpen && !details[id]) {
      try {
        const detReq = axios.get(`${API_BASE_URL}/proposals/details/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        let meets = { data: [] };
        try {
          meets = await axios.get(`${API_BASE_URL}/proposals/${id}/meetings`, { headers: { Authorization: `Bearer ${token}` } });
        } catch (e) {
          if (e?.response?.status !== 404) console.error('meetings load failed', e);
        }
        const det = await detReq;
        const apiDetails = det.data || {};
        const documents = normalizeDocuments(apiDetails.documents);
        setDetails((d) => ({ ...d, [id]: { ...apiDetails, documents, meetings: meets.data || [] } }));
      } catch (e) {
        console.error('details load failed', e);
      }
    }
  };

  const doAction = async (p, kind) => {
    const id = p.proposal_id;
    const note = notes[id] || '';
    setSaving((s) => ({ ...s, [id]: true }));
    try {
      const url = kind === 'approve'
        ? `${API_BASE_URL}/proposals/${id}/final-approve`
        : `${API_BASE_URL}/proposals/${id}/final-reject`;
      await axios.post(url, { note }, { headers: { Authorization: `Bearer ${token}` } });
      await fetchList();
    } catch (e) {
      alert(e?.response?.data?.error || 'Action failed');
    } finally {
      setSaving((s) => ({ ...s, [id]: false }));
    }
  };

  if (!items.length) {
    return (
      <div className="proposal-details-container">
        <div className="card"><div className="empty-state">No proposals ready for final approval.</div></div>
      </div>
    );
  }

  return (
    <div className="proposal-details-container">
      <div className="card">
        <div className="details-header header-row">
          <h2>Ready for Final Approval</h2>
          <span className="status-pill-header">{items.length} item(s)</span>
        </div>
      </div>

      {items.map((p) => {
        const id = p.proposal_id;
        const isOpen = !!expanded[id];
        const det = details[id];
        const hasMeeting = !!det?.meetings?.length;
        const isReady = (det?.proposal?.status || p.status) === 'Ready for Final Approval';
        const canFinal = hasMeeting && isReady;

        return (
          <div key={id} className="card" style={{ marginTop: 12 }}>
            <div className="details-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <strong style={{ fontSize: 16 }}>{p.title}</strong>
                <span className="status-pill">{p.custom_id || id}</span>
              </div>
              <button className="btn" onClick={() => toggleDetails(p)}>
                {isOpen ? 'Hide Details' : 'Show Details'}
              </button>
            </div>

            {isOpen && (
              <div className="card" style={{ marginTop: 8 }}>
                {det?.proposal && (
                  <div className="details-grid">
                    <div>
                      <div className="muted">Title</div>
                      <div className="value">{det.proposal.title}</div>
                    </div>
                    <div>
                      <div className="muted">Status</div>
                      <div className="value">{det.proposal.status}</div>
                    </div>
                    <div>
                      <div className="muted">Custom ID</div>
                      <div className="value">{det.proposal.custom_id || det.proposal.proposal_id}</div>
                    </div>
                    <div>
                      <div className="muted">Submission Date</div>
                      <div className="value">
                        {det.proposal.submission_date
                          ? new Date(det.proposal.submission_date).toLocaleDateString()
                          : '—'}
                      </div>
                    </div>
                  </div>
                )}

                {!!det?.documents?.length && (
                  <>
                    <div className="section-title" style={{ marginTop: 4 }}>Submitted Documents</div>
                    <div className="document-grid">
                      {det.documents.map((doc) => {
                        const href = doc.path
                          ? withToken(/^https?:\/\//.test(doc.path) ? doc.path : `${API_ORIGIN}${doc.path}`)
                          : '#';
                        return (
                          <a
                            key={doc.document_id}
                            className="document-tile clickable-tile"
                            href={href}
                            target="_blank"
                            rel="noreferrer"
                            title="Open document"
                          >
                            <span className="document-name">{doc.document_name}</span>
                          </a>
                        );
                      })}
                    </div>
                  </>
                )}

                {!!det?.meetings?.length && (
                  <>
                    <div className="section-title" style={{ marginTop: 16 }}>Meeting Timeline</div>
                    <ul className="comments-list">
                      {det.meetings.map((m) => (
                        <li key={m.id} className="comment-row">
                          <div className="comment-meta">
                            {new Date(m.meeting_date).toLocaleString()} • {m.decision}
                          </div>
                          <div className="comment-text">{m.note || '—'}</div>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}

            <div style={{ marginTop: 12 }}>
              <div className="section-title">Secretary note (final email)</div>
              <textarea
                rows={3}
                className="card"
                style={{ width: '100%', resize: 'vertical', marginTop: 6 }}
                placeholder="This note will be included in the final decision email"
                value={notes[id] || ''}
                onChange={(e) => setNotes((n) => ({ ...n, [id]: e.target.value }))}
              />
              <div className="actions-grid" style={{ marginTop: 12 }}>
                <button
                  className="btn"
                  disabled={!canFinal || !!saving[id]}
                  onClick={() => doAction(p, 'reject')}
                  title={!canFinal ? 'Disabled until meeting decision is recorded.' : undefined}
                >
                  {saving[id] ? 'Saving…' : 'Final Reject'}
                </button>
                <button
                  className="btn primary"
                  disabled={!canFinal || !!saving[id]}
                  onClick={() => doAction(p, 'approve')}
                  title={!canFinal ? 'Disabled until meeting decision is recorded.' : undefined}
                >
                  {saving[id] ? 'Saving…' : 'Final Approve'}
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
